import { useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, Pencil, Phone, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Permit } from '@/components/guards/Permit'
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  TaskStatusBadge,
  TaskPriorityBadge,
} from '@/components/shared/StatusBadges'
import { DeleteTaskDialog } from '@/components/tasks/DeleteTaskDialog'
import { TaskChecklistCard } from '@/components/tasks/TaskChecklistCard'
import { TaskComments } from '@/components/tasks/TaskComments'
import { TaskAttachments } from '@/components/tasks/TaskAttachments'
import { usePermissions } from '@/hooks/usePermissions'
import {
  useTask,
  useTaskChecklist,
  useUpdateTaskStatus,
} from '@/hooks/useTasks'
import { formatDate, formatDateTime, daysUntil } from '@/lib/format'
import { callClient, whatsappClient } from '@/lib/integrations'
import { canUpdateTaskStatus, isForbiddenError } from '@/lib/tasks'
import { toast } from '@/components/ui/toast'
import { getApiErrorMessage } from '@/services/api'
import { useAppSelector } from '@/store/hooks'
import {
  TASK_RELATED_TYPES,
  normalizeRelatedType,
  type TaskStatus,
} from '@/types'

const sectionCardClass =
  'overflow-visible rounded-[18px] border border-[#eceef2] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
const actionBtnClass =
  'h-10 rounded-xl border-[#dfe3ea] bg-white px-3 text-sm font-medium text-[#171a20] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-[#f8fafc]'
const deleteBtnClass =
  'h-10 w-10 rounded-xl border border-[#fecaca] bg-white p-0 text-[#dc2626] shadow-[0_1px_2px_rgba(15,23,42,0.04)] hover:bg-[#fef2f2]'
const actionBarShellClass =
  'sticky bottom-0 z-20 -mx-4 bg-[#F4F5F8] px-4 pb-4 pt-3'
const actionBarClass = 'grid grid-cols-[repeat(3,minmax(0,1fr))_40px] gap-2'
const statusOptions: TaskStatus[] = ['todo', 'in_progress', 'done', 'cancelled']

function SectionCard({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <section className={cn(sectionCardClass, className)}>{children}</section>
  )
}

function SectionHeading({
  title,
  trailing,
  action,
}: {
  title: string
  trailing?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className='flex items-center gap-2 overflow-hidden pr-4 pt-4 sm:pr-5'>
      <div className='flex min-w-0 flex-1 items-center border-l-4 border-primary px-4 py-1'>
        <div className='flex min-w-0 flex-1 items-center justify-between gap-3'>
          <h2 className='font-heading text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {title}
          </h2>
          {trailing}
        </div>
      </div>
      {action}
    </div>
  )
}

function DetailRow({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <div className='flex items-center gap-4 border-t border-[#f1f3f6] px-4 py-3 first:border-t-0 sm:px-5'>
      <span className='min-w-0 flex-1 text-sm font-normal leading-[1.4] text-[#3d434e]'>
        {label}
      </span>
      <div className='flex min-w-0 items-center justify-end text-right text-sm font-medium leading-[1.4] text-[#171a20]'>
        {children}
      </div>
    </div>
  )
}

interface TaskDetailDrawerContentProps {
  taskId: string
  onClose: () => void
}

export function TaskDetailDrawerContent({
  taskId,
  onClose,
}: TaskDetailDrawerContentProps) {
  const { t } = useTranslation('tasks')
  const location = useLocation()
  const navigate = useNavigate()
  const { data, isLoading, isError, error } = useTask(taskId)
  const { data: checklistData } = useTaskChecklist(taskId)
  const { canEdit, canDelete, canViewPath } = usePermissions()
  const viewer = useAppSelector(s => s.auth.user)
  const statusMutation = useUpdateTaskStatus(taskId)
  const [showDelete, setShowDelete] = useState(false)
  const openUploadRef = useRef<(() => void) | null>(null)

  if (isLoading) {
    return (
      <div className='flex h-full min-h-0 flex-col bg-[#f7f8fb]'>
        <div className='space-y-3 p-4'>
          <Skeleton className='h-36 w-full rounded-[18px]' />
          <Skeleton className='h-44 w-full rounded-[18px]' />
          <Skeleton className='h-40 w-full rounded-[18px]' />
        </div>
      </div>
    )
  }

  if (isError || !data?.data) {
    const forbidden = isForbiddenError(error)
    return (
      <div className='flex h-full min-h-0 items-center justify-center bg-[#f7f8fb] p-6'>
        <div className='space-y-4 text-center'>
          <p className='text-sm text-muted-foreground'>
            {forbidden ? t('detail.forbidden') : t('detail.notFound')}
          </p>
          <Button type='button' onClick={onClose}>
            {t('detail.backToTasks')}
          </Button>
        </div>
      </div>
    )
  }

  const task = data.data
  const checklistItems = checklistData?.data ?? []
  const completedChecklistCount = checklistItems.filter(
    item => item.is_done
  ).length
  const assignee = task.assignee
  const canEditTask = canEdit('tasks')
  const canDeleteTask = canDelete('tasks')
  const canUpdateStatus = canUpdateTaskStatus(task, viewer)
  const overdue =
    task.status !== 'done' &&
    daysUntil(task.due_date) !== null &&
    (daysUntil(task.due_date) as number) < 0

  const linkedMeta = TASK_RELATED_TYPES.find(
    r => r.value === normalizeRelatedType(task.related_type)
  )
  const linkedEntityPath =
    linkedMeta && task.related_id
      ? linkedMeta.value === 'job'
        ? `/calendar?job=${task.related_id}`
        : `${linkedMeta.path}/${task.related_id}`
      : null
  const canViewLinkedEntity = linkedEntityPath
    ? canViewPath(linkedEntityPath)
    : false
  const assigneeLabel = assignee
    ? (assignee.full_name ?? assignee.email ?? assignee.id)
    : task.assigned_to
      ? task.assigned_to.slice(0, 8)
      : null
  const statusHistory = task.status_history ?? []
  const latestHistoryStatus = statusHistory[0]?.properties?.new
  const shouldShowCurrentStatus =
    statusHistory.length === 0 || latestHistoryStatus !== task.status
  const hasInitialStatusHistory = statusHistory.some(
    entry =>
      entry.properties?.old === null || entry.properties?.old === undefined
  )
  const inferredInitialStatus =
    !hasInitialStatusHistory && statusHistory.length > 0
      ? statusHistory[statusHistory.length - 1]?.properties?.old
      : null

  const onCall = () => {
    const res = callClient(assignee?.phone)
    if (!res.ok) {
      toast({ variant: 'destructive', title: t('detail.noPhone') })
    }
  }

  const onWhatsapp = () => {
    const res = whatsappClient(assignee?.phone)
    if (!res.ok) {
      toast({ variant: 'destructive', title: t('detail.noPhone') })
    }
  }

  const updateStatus = async (nextStatus: TaskStatus) => {
    if (
      nextStatus === task.status ||
      !canUpdateStatus ||
      statusMutation.isPending
    ) {
      return
    }

    try {
      await statusMutation.mutateAsync({ status: nextStatus })
      toast({ variant: 'success', title: t('updateStatusDialog.updated') })
    } catch (e) {
      toast({
        variant: 'destructive',
        title: getApiErrorMessage(e, t('updateStatusDialog.updateError')),
      })
    }
  }

  return (
    <>
      <div className='flex h-full min-h-0 flex-col bg-[#f7f8fb]'>
        <div className='min-h-0 flex-1 overflow-y-auto px-4 pb-0 pt-4'>
          <div className='mx-auto w-full max-w-[680px] space-y-3'>
            <section className='relative overflow-hidden rounded-[18px] border border-[#eceef2] bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]'>
              <div className='relative px-4 py-4 sm:px-5 sm:pl-7 rounded-[16px] border-l-6 border-[#FF5A1F]'>
                <div className='flex flex-wrap items-center gap-2'>
                  <TaskStatusBadge status={task.status} />
                  <TaskPriorityBadge priority={task.priority} />
                </div>
                <h1 className='mt-3 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
                  {task.title}
                </h1>
                <p className='mt-1 text-xs font-normal leading-4 text-[#3d434e]'>
                  {task.id.slice(0, 8).toUpperCase()} · {t('detail.created')}{' '}
                  {formatDate(task.created_at)}
                </p>
                <div className='mt-4 flex flex-wrap gap-2'>
                  {statusOptions.map(status => {
                    const active = task.status === status
                    const enabled =
                      canUpdateStatus && !active && !statusMutation.isPending
                    return (
                      <button
                        key={status}
                        type='button'
                        disabled={!enabled}
                        className={cn(
                          'rounded-lg border px-3 py-2 text-xs font-normal leading-4 transition-colors',
                          active
                            ? 'border-[#9adbc8] bg-[#ecfdf5] text-[#0f766e]'
                            : 'border-[#e5e7eb] bg-white text-[#4b5563]',
                          enabled
                            ? 'cursor-pointer hover:border-[#cbd5e1] hover:bg-[#f8fafc]'
                            : 'cursor-default'
                        )}
                        onClick={() => updateStatus(status)}
                      >
                        {t(`enums:taskStatus.${status}`)}
                      </button>
                    )
                  })}
                </div>
              </div>
            </section>

            <SectionCard>
              <SectionHeading title={t('detail.information')} />
              <div className='px-4 pb-2 pt-1 sm:px-5'>
                <p className='pb-3 text-sm font-medium leading-[1.4] text-[#3d434e]'>
                  {task.description || '--'}
                </p>
              </div>
              <div className='pb-1'>
                <DetailRow label={t('detail.assignedTo')}>
                  {assigneeLabel ?? t('detail.unassigned')}
                </DetailRow>
                <DetailRow label={t('detail.dueDate')}>
                  <span className={overdue ? 'text-[#dc2626]' : ''}>
                    {formatDate(task.due_date)}
                  </span>
                </DetailRow>
                <DetailRow label={t('detail.priority')}>
                  <TaskPriorityBadge priority={task.priority} />
                </DetailRow>
                <DetailRow label={t('detail.linkedEntity')}>
                  {linkedMeta && task.related_id ? (
                    canViewLinkedEntity ? (
                      <button
                        type='button'
                        className='inline-flex items-center gap-1 text-[#ff5a1f] hover:underline'
                        onClick={() => navigate(linkedEntityPath as string)}
                      >
                        <span>{linkedMeta.label}</span>
                        <ChevronRight className='h-4 w-4' />
                      </button>
                    ) : (
                      <span className='text-[#ff5a1f]'>{linkedMeta.label}</span>
                    )
                  ) : (
                    emptyValue
                  )}
                </DetailRow>
              </div>
            </SectionCard>

            <TaskChecklistCard
              taskId={task.id}
              completed={completedChecklistCount}
              total={checklistItems.length}
              readOnly={!canEditTask}
            />

            <SectionCard>
              <SectionHeading title={t('detail.statusHistory')} />
              <div className='px-4 pb-4 pt-1 sm:px-5'>
                <div className='space-y-3'>
                  {statusHistory.length > 0 ? (
                    <>
                      {statusHistory.map(entry => {
                        const from = entry.properties?.old
                        const to = entry.properties?.new
                        return (
                          <div
                            key={entry.id}
                            className='flex items-center justify-between gap-3 rounded-xl bg-[#f9fafb] px-3 py-3 text-sm'
                          >
                            <div className='flex min-w-0 items-center gap-2'>
                              <TaskStatusBadge status={to as TaskStatus} />
                              <span className='truncate font-medium text-[#171a20]'>
                                {typeof from === 'string' && from !== '' ? (
                                  <>
                                    {t(
                                      `enums:taskStatus.${from as TaskStatus}`
                                    )}{' '}
                                    <span aria-hidden='true'>→</span>{' '}
                                  </>
                                ) : null}
                                {typeof to === 'string'
                                  ? t(`enums:taskStatus.${to as TaskStatus}`)
                                  : '--'}
                              </span>
                            </div>
                            <div className='shrink-0 text-right font-medium text-[#3d434e]'>
                              <div>{entry.user ?? '—'}</div>
                              <div>
                                {formatDateTime(
                                  entry.timestamp ?? entry.created_at
                                )}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                      {typeof inferredInitialStatus === 'string' &&
                      inferredInitialStatus !== '' ? (
                        <div className='flex items-center justify-between gap-3 rounded-xl bg-[#f9fafb] px-3 py-3 text-sm'>
                          <div className='flex min-w-0 items-center gap-2'>
                            <TaskStatusBadge
                              status={inferredInitialStatus as TaskStatus}
                            />
                            <span className='truncate font-medium text-[#171a20]'>
                              {t(
                                `enums:taskStatus.${inferredInitialStatus as TaskStatus}`
                              )}
                            </span>
                          </div>
                          <div className='shrink-0 text-right font-medium text-[#3d434e]'>
                            <div>{t('detail.created')}</div>
                            <div>{formatDateTime(task.created_at)}</div>
                          </div>
                        </div>
                      ) : null}
                      {shouldShowCurrentStatus ? (
                        <div className='flex items-center justify-between gap-3 rounded-xl bg-[#f9fafb] px-3 py-3 text-sm'>
                          <div className='flex min-w-0 items-center gap-2'>
                            <TaskStatusBadge status={task.status} />
                            <span className='truncate font-medium text-[#171a20]'>
                              {t(`enums:taskStatus.${task.status}`)}
                            </span>
                          </div>
                          <div className='shrink-0 text-right font-medium text-[#3d434e]'>
                            <div>{t('detail.currentStatus')}</div>
                            <div>{formatDateTime(task.updated_at)}</div>
                          </div>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className='flex items-center justify-between gap-3 rounded-xl bg-[#f9fafb] px-3 py-3 text-sm'>
                      <div className='flex min-w-0 items-center gap-2'>
                        <TaskStatusBadge status={task.status} />
                        <span className='truncate font-medium text-[#171a20]'>
                          {t(`enums:taskStatus.${task.status}`)}
                        </span>
                      </div>
                      <div className='shrink-0 text-right font-medium text-[#3d434e]'>
                        <div>{t('detail.currentStatus')}</div>
                        <div>{formatDateTime(task.updated_at)}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </SectionCard>

            <SectionCard>
              <SectionHeading title={t('attachments.title')} />
              <div className='px-4 pb-4 pt-1 sm:px-5'>
                <TaskAttachments
                  taskId={task.id}
                  detailPage
                  readOnly={!canEditTask}
                  onUploadReady={open => {
                    openUploadRef.current = open
                  }}
                />
              </div>
            </SectionCard>

            <SectionCard>
              <SectionHeading title={t('detail.internalNotes')} />
              <div className='px-4 pb-4 pt-1 sm:px-5'>
                <TaskComments
                  taskId={task.id}
                  readOnly={!canEditTask}
                  variant='internalNotes'
                  textareaClassName='border-transparent'
                />
              </div>
            </SectionCard>

            <div className={actionBarShellClass}>
              <div className={actionBarClass}>
                {canEditTask ? (
                  <Permit module='tasks' action='edit'>
                    <Button
                      type='button'
                      variant='outline'
                      className={actionBtnClass}
                      onClick={() =>
                        navigate({
                          pathname: `/tasks/${task.id}/edit`,
                          search: location.search,
                        })
                      }
                    >
                      <Pencil className='mr-2 h-4 w-4' />
                      {t('detail.edit')}
                    </Button>
                  </Permit>
                ) : (
                  <div />
                )}
                <Button
                  type='button'
                  variant='outline'
                  className={actionBtnClass}
                  onClick={onWhatsapp}
                >
                  <WhatsAppIcon className='mr-2' />
                  {t('detail.whatsapp')}
                </Button>
                <Button
                  type='button'
                  variant='outline'
                  className={actionBtnClass}
                  onClick={onCall}
                >
                  <Phone className='mr-2 h-4 w-4' />
                  {t('detail.call')}
                </Button>
                {canDeleteTask ? (
                  <Permit module='tasks' action='delete'>
                    <Button
                      type='button'
                      variant='outline'
                      className={deleteBtnClass}
                      onClick={() => setShowDelete(true)}
                    >
                      <Trash2 className='h-4 w-4' />
                    </Button>
                  </Permit>
                ) : (
                  <div />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <DeleteTaskDialog
        task={showDelete ? task : null}
        onClose={() => setShowDelete(false)}
        onDeleted={onClose}
      />
    </>
  )
}

const emptyValue = <span>--</span>

export default TaskDetailDrawerContent
